import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SaveIcon, Trash2Icon, UserPenIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE_URL } from '@/constants';
import { NbButton, NbCard, NbField, NbPageHeader } from '@/components/Neobrut';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';
import { useAuthToken } from '@/hooks/useAuthToken';
import { MyProfileSkeleton } from '@/components/Skeletons';

interface UserProfileFormData {
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  address: string;
}

export const MyProfile: FC = () => {
  const { token, isTokenExpired, userIdFromToken } = useAuthToken();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [initialValuesSet, setInitialValuesSet] = useState(false);
  const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  const {
    data: users,
    isError,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['user'],
    queryFn: async (): Promise<Array<User>> => {
      if (!token || isTokenExpired(token)) {
        toast.info('Tu sesión ha expirado. Iniciá sesión nuevamente.');
        navigate('/login');
        return [];
      }
      const response = await axios.get(`${API_BASE_URL}/users/?id=${userIdFromToken(token)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UserProfileFormData>();

  const mutation = useMutation({
    mutationFn: (data: UserProfileFormData) =>
      axios.put(`${API_BASE_URL}/users/${userId}/`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  });

  const onSubmit = (data: UserProfileFormData) => {
    if (!token || isTokenExpired(token)) {
      toast.info('Tu sesión ha expirado. Iniciá sesión nuevamente.');
      navigate('/login');
      return;
    }
    mutation.mutate(data, {
      onSuccess: () => {
        setIsEditing(false);
        refetch();
      },
      onError: (error) => console.error('Error updating profile:', error),
    });
  };

  useEffect(() => {
    if (users && users.length > 0 && !initialValuesSet) {
      const [u] = users;
      setValue('email', u.email);
      setValue('first_name', u.first_name);
      setValue('last_name', u.last_name);
      setValue('phone_number', u.phone_number);
      setValue('address', u.address);
      setInitialValuesSet(true);
    }
  }, [users, setValue, initialValuesSet]);

  const handleDeleteAccount = () => {
    axios
      .delete(`${API_BASE_URL}/users/${userId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        navigate('/login');
        localStorage.removeItem('access_token');
      })
      .catch((error) => console.error('Error deleting account:', error));
  };

  useEffect(() => {
    if (!token || isTokenExpired(token)) {
      toast.info('Tu sesión ha expirado. Iniciá sesión nuevamente.');
      navigate('/login');
      return;
    }
    setUserId(userIdFromToken(token));
  }, [token, isTokenExpired, navigate, userIdFromToken]);

  if (isLoading) return <MyProfileSkeleton />;

  return (
    <div className="mx-auto w-full max-w-md pb-10">
      <NbPageHeader
        slug="/ mis datos"
        title={
          <>
            Tu perfil,
            <br />
            <span className="nb-highlight">editable.</span>
          </>
        }
        tone="plain"
      />

      {isError && <div className="px-4 py-16 text-center">Error: {error.message}</div>}

      {users && (
        <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4">
          <NbCard className="flex flex-col gap-4 p-4">
            <NbField
              id="email"
              label="Email"
              type="email"
              disabled
              error={errors.email?.message}
              {...register('email', { required: 'Email es requerido' })}
            />
            <NbField
              id="first_name"
              label="Nombre"
              type="text"
              disabled={!isEditing}
              error={errors.first_name?.message}
              {...register('first_name', { required: 'Nombre es requerido' })}
            />
            <NbField
              id="last_name"
              label="Apellido"
              type="text"
              disabled={!isEditing}
              error={errors.last_name?.message}
              {...register('last_name', { required: 'Apellido es requerido' })}
            />
            <NbField
              id="phone_number"
              label="Teléfono"
              type="tel"
              disabled={!isEditing}
              error={errors.phone_number?.message}
              {...register('phone_number', { required: 'Número de teléfono es requerido' })}
            />
            <NbField
              id="address"
              label="Dirección"
              type="text"
              disabled={!isEditing}
              error={errors.address?.message}
              {...register('address')}
            />

            {isEditing ? (
              <NbButton fill="ink" type="submit" className="w-full py-3">
                <SaveIcon size={14} /> Guardar
              </NbButton>
            ) : (
              <NbButton
                fill="ink"
                type="button"
                className="w-full py-3"
                onClick={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
              >
                <UserPenIcon size={14} /> Editar
              </NbButton>
            )}
          </NbCard>

          <div className="pt-4">
            <NbButton
              fill="urgent"
              type="button"
              className="w-full py-3"
              onClick={() => setIsDeleteAccountDialogOpen(true)}
            >
              <Trash2Icon size={14} /> Eliminar cuenta
            </NbButton>
          </div>

          <ConfirmationDialog
            onClose={() => setIsDeleteAccountDialogOpen(false)}
            open={isDeleteAccountDialogOpen}
            onConfirm={handleDeleteAccount}
            title="¿Estás seguro que querés eliminar tu cuenta?"
            description="Esta acción no se puede deshacer. Todos tus datos y publicaciones van a ser eliminados."
          />
        </form>
      )}
    </div>
  );
};
